import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2 } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import Swal from "sweetalert2";
import { apiErrorMessage } from "Common/constants/apiErrors";
import useServerSorting from "Common/hooks/useServerSorting";

const Index = () => {
  const [data, setData] = useState<any[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [eventData, setEventData] = useState<any>(null);
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);
  const navigator = useNavigate()
  const sorting = useServerSorting();

  // دریافت لیست انواع محصول از API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("https://portal-api.mahanpanel.com/product-types/", { params: sorting.params });
        // پاسخ مستقیماً یک آرایه است
        setData(res.data);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching product types:", error);
        if (error.status === 401) {
          return navigator("/login")
        }
        setErrorMessage(
          "مشکلی در ارتباط با سرور رخ داده است. لطفاً اینترنت خود را بررسی کنید."
        );
      }
    };
    fetchData();
  }, [reload, sorting.sortBy, sorting.sortOrder]);

  // حذف نوع محصول
  const onClickDelete = (row: any) => {
    setDeleteModal(true);
    setEventData(row);
  };

  const handleDelete = async () => {
    if (!eventData) return;
    try {
      const res = await axios.delete(
        `https://portal-api.mahanpanel.com/product-types/${eventData.id}`
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "نوع محصول با موفقیت حذف شد", "success");
        setData((prev) => prev.filter((item) => item.id !== eventData.id));
        setDeleteModal(false);
        setEventData(null);
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      Swal.fire(
        "عملیات ناموفق",
        apiErrorMessage(error, "خطا در حذف نوع محصول"),
        "error"
      );
      setDeleteModal(false);
      setEventData(null);
    }
  };

  // ثبت نوع محصول جدید
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "", // مطابق مستندات فیلد name است
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("لطفاً نام نوع محصول را وارد کنید")
        .min(2, "حداقل ۲ کاراکتر"),
    }),
    onSubmit: async (values) => {
      try {
        const res = await axios.post(
          "https://portal-api.mahanpanel.com/product-types/",
          values
        );
        if (res.status === 201) {
          Swal.fire("عملیات موفق", `نوع محصول "${values.name}" ثبت شد`, "success");
          setReload(!reload);
          toggle(); // بستن مدال
        }
      } catch (error: any) {
        console.error("Create error:", error);
        if (error.response?.status === 422) {
          Swal.fire("خطا", apiErrorMessage(error, "نام نوع محصول تکراری یا نامعتبر است"), "error");
        } else {
          Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت نوع محصول"), "error");
        }
      }
    },
  });

  const toggle = useCallback(() => {
    setShow((prev) => !prev);
    if (!show) {
      validation.resetForm();
      setEventData(null);
    }
  }, [show, validation]);

  const handleSubmitButton = (e: React.MouseEvent) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "id",
        cell: (cell: any) => <span>{cell.getValue()}</span>,
      },
      {
        header: "نوع محصول",
        accessorKey: "name", // مطابق مستندات name
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link
            to="#!"
            className="transition-all duration-150 ease-linear text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <div className="flex gap-3">
            <Link to="#!" onClick={() => onClickDelete(cell.row.original)}>
              <Trash2 className="size-4 text-red-500 hover:text-red-700" />
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="ثبت نوع محصول" title="داده‌های اولیه" />
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      {errorMessage && (
        <div className="text-center text-red-500 mb-3">{errorMessage}</div>
      )}
      <div className="card" id="employeeTable rtl">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع انواع محصول (<b className="total-Employs">{data.length}</b>)
            </h6>
            <div className="shrink-0">
              <Link
                to="#!"
                onClick={toggle}
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
              >
                <span className="align-middle rtl">ثبت نوع محصول جدید</span>{" "}
                <Plus className="inline-block size-4 rtl" />
              </Link>
            </div>
          </div>
          {data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data}
              serverSorting={{ sortBy: sorting.sortBy, sortOrder: sorting.sortOrder, onChange: sorting.onChange }}
              customPageSize={10}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter
              count={errorMessage ? 1 : 15}
              text={errorMessage || "داده‌ای در جدول وجود ندارد"}
            />
          )}
        </div>
      </div>

      {/* مودال ثبت نوع محصول */}
      <Modal
        show={show}
        onHide={toggle}
        modal-center="true"
        dialogClassName="w-screen md:w-[30rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">اضافه کردن نوع محصول</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form
            className="create-form"
            id="create-form"
            onSubmit={validation.handleSubmit}
          >
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <div className="xl:col-span-11 dark:text-white text-gray-800">
                <div className="flex items-center">
                  <label
                    htmlFor="name"
                    className="text-base font-medium mr-2 w-1/4"
                  >
                    نوع محصول:
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="form-input font-[12px] flex-1"
                    placeholder="مثلاً کالا، خدمت، کالا دارایی"
                    name="name"
                    autoComplete="off"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name}
                  />
                </div>
                {validation.touched.name && validation.errors.name && (
                  <p className="text-red-400 mr-24 mt-2">
                    {typeof validation.errors.name === "string"
                      ? validation.errors.name
                      : ""}
                  </p>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleSubmitButton}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:ring focus:ring-custom-100 dark:focus:ring-custom-400/20"
          >
            ثبت
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
